import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, X, Copy, Check } from "lucide-react";

const ROLES = ["", "buyer", "seller", "admin"];

export default function AdminCreateUserForm({ api, t, onCreated }) {
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "", organization_id: "" });
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/organizations").then((r) => setOrgs(r.data || [])).catch(() => {});
  }, [open, api]);

  const reset = () => {
    setForm({ email: "", name: "", password: "", role: "", organization_id: "" });
    setTempPw(null);
    setCopied(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { email: form.email.trim().toLowerCase() };
      if (form.name.trim()) body.name = form.name.trim();
      if (form.password.trim()) body.password = form.password;
      if (form.role) body.role = form.role;
      if (form.organization_id) body.organization_id = form.organization_id;
      const { data } = await api.post("/admin/users", body);
      toast.success(t("admin.createUser.created"));
      if (onCreated) onCreated(data.user);
      if (data.temp_password) {
        setTempPw(data.temp_password);
      } else {
        setOpen(false);
        reset();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    } finally {
      setBusy(false);
    }
  };

  const copyPw = async () => {
    try {
      await navigator.clipboard.writeText(tempPw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      toast.error("Copy failed");
    }
  };

  const close = () => { setOpen(false); reset(); };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="btn-primary" data-testid="admin-create-user-btn">
        <UserPlus size={14} /> {t("admin.createUser.btn")}
      </button>
    );
  }

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5 mb-4" data-testid="admin-create-user-form">
      <div className="flex items-center justify-between mb-4">
        <div className="font-display text-lg">{t("admin.createUser.title")}</div>
        <button onClick={close} className="btn-ghost" data-testid="admin-create-user-close"><X size={14} /></button>
      </div>
      {tempPw ? (
        <div className="border border-[var(--moss)] bg-[var(--moss)]/10 p-4">
          <div className="label-tech mb-2">{t("admin.createUser.tempPwTitle")}</div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-[var(--bone)] border border-[var(--ink-200)] px-3 py-2 font-mono text-sm" data-testid="admin-create-user-temp-pw">{tempPw}</code>
            <button onClick={copyPw} className="btn-ghost" data-testid="admin-create-user-copy">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
          <p className="label-tech mt-3">{t("admin.createUser.tempPwHelp")}</p>
          <button onClick={close} className="btn-primary mt-4" data-testid="admin-create-user-done">{t("admin.createUser.done")}</button>
        </div>
      ) : (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="label-tech mb-1">{t("admin.createUser.email")} *</div>
            <input type="email" required className="input-tech" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="namn@foretag.se" data-testid="admin-create-user-email" />
          </div>
          <div>
            <div className="label-tech mb-1">{t("admin.createUser.name")}</div>
            <input className="input-tech" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-create-user-name" />
          </div>
          <div>
            <div className="label-tech mb-1">{t("admin.createUser.password")}</div>
            <input className="input-tech" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t("admin.createUser.passwordPlaceholder")} data-testid="admin-create-user-password" />
          </div>
          <div>
            <div className="label-tech mb-1">{t("admin.createUser.role")}</div>
            <select className="input-tech" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="admin-create-user-role">
              <option value="">{t("admin.createUser.rolePending")}</option>
              {ROLES.filter((r) => r).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <div className="label-tech mb-1">{t("admin.createUser.org")}</div>
            <select className="input-tech" value={form.organization_id} onChange={(e) => setForm({ ...form, organization_id: e.target.value })} data-testid="admin-create-user-org">
              <option value="">{t("admin.createUser.orgPersonal")}</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name} · {o.subscription}</option>)}
            </select>
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <button type="button" onClick={close} className="btn-ghost">{t("admin.orgs.cancel")}</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="admin-create-user-submit">
              {busy ? "…" : t("admin.createUser.submit")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
